import {apolloClient, gql} from '../graphql';
import {logAndThrowGraphQLErrors, normalizeGraphQLObject} from '../graphql/helpers';
import {DateTime} from 'luxon';
import {formatIsoDate} from 'shared/src/datetime';
import type {ID, Sale, SaleItem} from 'shared/src/models';

type NewSale = Omit<Sale, 'id' | 'items'> & {
	items: Array<Omit<SaleItem, 'id'>>;
};

export const createSale = async (newSale: NewSale): Promise<Sale> => {
	const {data, errors} = await apolloClient.mutate({
		mutation: gql(/* GraphQL */ `
			mutation CreateSale($newSale: NewSaleInput!) {
				createSale(newSale: $newSale) {
					id
					note
					transaction {
						id
					}
					items {
						id
						receivedAmount
						faceValue
						taxAmount
						quantity
						note
						product {
							id
						}
						productVariant {
							id
						}
					}
				}
			}
		`),
		variables: {
			newSale: {
				note: newSale.note,
				transactionId: newSale.transaction,
				items: newSale.items.map((item) => ({
					receivedAmount: item.receivedAmount,
					faceValue: item.faceValue,
					taxAmount: item.taxAmount,
					quantity: item.quantity,
					note: item.note,
					productId: item.product,
					productVariantId: item.productVariant,
				})),
			},
		},
	});

	if (errors && errors.length > 0) {
		logAndThrowGraphQLErrors(errors);
	}

	return {
		...normalizeGraphQLObject(data!.createSale),
		transaction: data!.createSale.transaction.id,
		items: data!.createSale.items.map((item) => ({
			...normalizeGraphQLObject(item),
			product: item.product?.id ?? null,
			productVariant: item.productVariant?.id ?? null,
		})),
	};
};

export const getSales = async ({
	startDate,
	endDate,
}: {
	startDate: DateTime;
	endDate: DateTime;
}): Promise<Sale[]> => {
	const {data, error} = await apolloClient.query({
		query: gql(/* GraphQL */ `
			query GetSales($startDate: String!, $endDate: String!) {
				sales(startDate: $startDate, endDate: $endDate) {
					id
					note
					transaction {
						id
						memo
						amount
					}
					items {
						id
						receivedAmount
						faceValue
						taxAmount
						quantity
						note
						product {
							id
						}
						productVariant {
							id
						}
					}
				}
			}
		`),
		variables: {
			startDate: formatIsoDate(startDate),
			endDate: formatIsoDate(endDate),
		},
		fetchPolicy: 'no-cache',
	});

	if (error) {
		throw error;
	}

	return data.sales.map((sale) => ({
		...normalizeGraphQLObject(sale),
		transaction: sale.transaction.id,
		items: sale.items.map((item) => ({
			...normalizeGraphQLObject(item),
			product: item.product?.id ?? null,
			productVariant: item.productVariant?.id ?? null,
		})),
	}));
};

export const deleteSale = async (saleId: ID): Promise<void> => {
	const {errors} = await apolloClient.mutate({
		mutation: gql(/* GraphQL */ `
			mutation DeleteSale($saleId: ID!) {
				deleteSale(id: $saleId)
			}
		`),
		variables: {
			saleId,
		},
	});

	if (errors && errors.length > 0) {
		logAndThrowGraphQLErrors(errors);
	}
};
